import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Divider,
  Typography,
  colors,
  Box,
  InputAdornment,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  addOtherExpensesMargin,
  updateOtherExpensesMargin,
  getOtherExpenseMargins,
  getOtherExpenseCategories,
  OtherExpensesMargin,
  OtherExpenseRateFormType,
  buildOtherExpenseCategoryOptions,
  otherExpenseCategoryKey,
  parseOtherExpenseCategoryKey,
} from 'api/otherExpensesApi';
import { listClients } from 'api/clientsApi';
import { getBranches } from 'api/branchApi';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import BranchAutocomplete from '../Utils/BranchAutocomplete';
import DuplicateConfirmationDialog from '../LabourSection/DuplicateConfirmationDialog';
import { checkOtherExpenseRateDuplication } from '../Utils/Utils';
import { useSnackbar } from 'notistack';

interface AddOrEditOtherExpenseMarginDialogProps {
  openDialog: boolean;
  onClose: () => void;
  editItem: OtherExpensesMargin | null;
  onClearEdit?: () => void;
}

// The category select holds one key for default and custom categories alike
// (see otherExpenseCategoryKey); it is split back into type + id on submit.
type MarginFormValues = Omit<
  OtherExpenseRateFormType,
  'otherExpenseType' | 'otherExpenseCategoryId'
> & { categoryKey: string };

export default function AddOrEditOtherExpenseMarginDialog({
  openDialog,
  onClose,
  editItem,
  onClearEdit,
}: AddOrEditOtherExpenseMarginDialogProps) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const clients = useQuery(['clients'], listClients);
  const { data: branches } = useQuery(['branches'], getBranches);
  const isEdit = !!editItem;
  const [duplicateDialogConfirmOpen, setDuplicateDialogConfirmOpen] =
    useState(false);
  const [duplicatingRateId, setDuplicatingRateId] = useState<string | null>(
    null
  );

  const { data: otherExpenseData } = useQuery(
    ['other-expenses-margin'],
    getOtherExpenseMargins
  );
  const { data: customCategories } = useQuery(
    ['other-expense-categories', { includeArchived: true }],
    () => getOtherExpenseCategories({ includeArchived: true })
  );

  // Archived categories aren't offered, except the one a margin being edited
  // already belongs to.
  const categoryOptions = buildOtherExpenseCategoryOptions(
    (customCategories || []).filter(
      (c) => !c.isArchived || c.id === editItem?.otherExpenseCategoryId
    )
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<MarginFormValues>();

  const toRateFormData = (
    data: MarginFormValues
  ): OtherExpenseRateFormType => ({
    ...parseOtherExpenseCategoryKey(data.categoryKey),
    margin: data.margin,
    effectiveFrom: data.effectiveFrom,
    clientId: data.isDefault ? null : data.clientId ? data.clientId : null,
    branchId: data.isDefault ? null : data.branch?.id ? data.branch.id : null,
    isDefault: data.isDefault,
    removableItemId: duplicatingRateId ? editItem?.id : null,
  });

  const effectiveFrom = watch('effectiveFrom');
  const isDefault = watch('isDefault');
  const clientId = watch('clientId');
  const branch = watch('branch');

  const addMutation = useMutation(addOtherExpensesMargin, {
    onSuccess: () => {
      queryClient.invalidateQueries(['other-expenses-margin']);
      onClose();
      reset();
      onClearEdit?.();
      enqueueSnackbar('Margin added successfully', {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar('Failed to add margin', {
        variant: 'error',
      });
    },
  });

  const updateMutation = useMutation(updateOtherExpensesMargin, {
    onSuccess: () => {
      queryClient.invalidateQueries(['other-expenses-margin']);
      onClose();
      reset();
      onClearEdit?.();
      enqueueSnackbar('Margin updated successfully', {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar('Failed to update margin', {
        variant: 'error',
      });
    },
  });

  useEffect(() => {
    if (openDialog && editItem) {
      reset({
        categoryKey: otherExpenseCategoryKey(
          editItem.otherExpenseType,
          editItem.otherExpenseCategoryId
        ),
        margin: Number(editItem.margin),
        clientId: editItem.clientId,
        branchId: editItem.branchId,
        branch: branches?.find((b) => b.id === editItem.branchId) ?? null,
        isDefault: editItem.isDefault ?? false,
        effectiveFrom: editItem.effectiveFrom.slice(0, 10),
      });
    } else if (openDialog) {
      reset({
        effectiveFrom: new Date().toISOString(),
        isDefault: false,
        margin: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when dialog open or edit data changes
  }, [openDialog, editItem]);

  const onSubmit = (data: MarginFormValues) => {
    const submitData = toRateFormData(data);

    const { checkRateDuplicate, foundRateId } =
      checkOtherExpenseRateDuplication(submitData, otherExpenseData || []);

    if (checkRateDuplicate && foundRateId !== editItem?.id) {
      setDuplicateDialogConfirmOpen(true);
      setDuplicatingRateId(foundRateId);
      return;
    }

    if (editItem) {
      updateMutation.mutate({
        id: editItem.id,
        data: submitData,
      });
    } else {
      addMutation.mutate({ data: submitData });
    }
  };

  const isSubmitting = addMutation.isLoading || updateMutation.isLoading;

  return (
    <Dialog
      open={openDialog}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth>
      <DuplicateConfirmationDialog
        alertMessage={`You already have a margin for the selected category on the effective date. Do you want to override the existing margin or cancel to modify the effective date?`}
        isUpdatingRate={updateMutation.isLoading}
        onConfirm={() => {
          setDuplicateDialogConfirmOpen(false);
          if (duplicatingRateId) {
            updateMutation.mutate({
              id: duplicatingRateId,
              data: {
                ...parseOtherExpenseCategoryKey(watch('categoryKey')),
                margin: watch('margin'),
                clientId: isDefault ? null : watch('clientId'),
                branchId: isDefault ? null : watch('branch')?.id,
                isDefault: watch('isDefault'),
                effectiveFrom: watch('effectiveFrom'),
                removableItemId: duplicatingRateId ? editItem?.id : null,
              },
            });
          }
        }}
        openConfirmationDialog={duplicateDialogConfirmOpen}
        setOpenConfirmationDialog={setDuplicateDialogConfirmOpen}
      />
      <DialogTitle sx={{ m: 1 }}>
        {isEdit ? 'Edit Margin' : 'Add Margin'}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="categoryKey"
                  control={control}
                  rules={{ required: 'Category is required' }}
                  render={(field) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      select
                      label="Category"
                      fullWidth
                      required
                      error={!!errors.categoryKey}
                      helperText={errors.categoryKey?.message}>
                      {categoryOptions.map((cat) => (
                        <MenuItem key={cat.key} value={cat.key}>
                          {cat.label}
                          {cat.category?.isArchived ? ' (archived)' : ''}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="margin"
                  control={control}
                  rules={{
                    required: 'Margin is required',
                    min: {
                      value: 0,
                      message: 'Margin must be 0 or greater',
                    },
                  }}
                  render={(field) => (
                    <TextField
                      {...field}
                      label="Margin"
                      type="number"
                      fullWidth
                      required
                      error={!!errors.margin}
                      helperText={errors.margin?.message}
                      inputProps={{ step: '0.01', min: '0' }}
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || 0)
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">%</InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    backgroundColor: '#f5f5f5',
                    padding: '1rem',
                    borderRadius: '5px',
                  }}>
                  <Controller
                    name="isDefault"
                    control={control}
                    render={(field) => (
                      <FormControlLabel
                        label={`Use as Default Margin`}
                        control={
                          <Checkbox
                            checked={field.value ?? false}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setValue('branch', null);
                                setValue('clientId', null);
                              }
                              field.onChange(e.target.checked);
                            }}
                            color="primary"
                          />
                        }
                      />
                    )}
                  />
                </Box>
              </Grid>
              {!isDefault && (
                <Grid item xs={12}>
                  <BranchAutocomplete
                    name="branch"
                    label="Select Branch"
                    control={control}
                    rules={{
                      required:
                        !isDefault && !clientId
                          ? {
                              value: true,
                              message:
                                'Branch selection is required if Client is not selected',
                            }
                          : false,
                    }}
                    errors={errors}
                    branches={branches}
                    branchId={branch?.id}
                  />
                </Grid>
              )}
              {!isDefault && (
                <Grid item xs={12}>
                  <Controller
                    name="clientId"
                    rules={{
                      required:
                        !isDefault && !branch
                          ? {
                              value: true,
                              message:
                                'Client selection is required if Branch is not selected',
                            }
                          : false,
                    }}
                    control={control}
                    render={(field) => (
                      <Autocomplete
                        options={clients.data || []}
                        getOptionLabel={(option) => option.legalName || ''}
                        value={
                          clients.data?.find((c) => c.id === field.value) ??
                          null
                        }
                        onChange={(_, value) =>
                          field.onChange(value?.id ?? undefined)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Client"
                            fullWidth
                            error={!!errors.clientId}
                            helperText={errors.clientId?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  sx={{ mb: '0.5rem', pl: 0.5, color: colors.grey[600] }}>
                  Effective From
                </Typography>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <Controller
                    control={control}
                    name={'effectiveFrom'}
                    rules={{
                      required: {
                        value: true,
                        message: 'Effective From date is required',
                      },
                    }}
                    render={() => {
                      return (
                        <DatePicker
                          autoOk
                          fullWidth
                          orientation="landscape"
                          error={Boolean(errors.effectiveFrom)}
                          variant="static"
                          helperText={errors?.effectiveFrom?.message}
                          openTo="date"
                          value={effectiveFrom}
                          defaultValue={
                            effectiveFrom ? effectiveFrom : new Date()
                          }
                          onChange={(e) => setValue('effectiveFrom', e)}
                        />
                      );
                    }}
                  />
                </MuiPickersUtilsProvider>
                {errors.effectiveFrom && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ paddingTop: 1, display: 'block' }}>
                    {errors.effectiveFrom.message}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ mx: 2, my: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: grey[700],
            mr: 1,
          }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Margin'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
